/**
 * Helpers for AI assistant ↔ Editor.js (RichParagraph uses HTML in `data.text`).
 */

/** Strip tags to plain text (browser). */
export function stripHtml(html) {
  if (html == null || html === '') return '';
  if (typeof html !== 'string') return String(html);
  const d = typeof document !== 'undefined' ? document.createElement('div') : null;
  if (!d) return html.replace(/<[^>]+>/g, ' ');
  d.innerHTML = html;
  return d.textContent || d.innerText || '';
}

function listItemsToText(items, depth = 0) {
  if (!Array.isArray(items)) return '';
  const indent = '  '.repeat(depth);
  return items
    .map((item) => {
      if (typeof item === 'string') return `${indent}• ${stripHtml(item)}`;
      if (item && typeof item === 'object') {
        const content = item.content != null ? stripHtml(String(item.content)) : '';
        const nested = item.items ? listItemsToText(item.items, depth + 1) : '';
        return [content ? `${indent}• ${content}` : '', nested].filter(Boolean).join('\n');
      }
      return '';
    })
    .filter(Boolean)
    .join('\n');
}

/**
 * Extract searchable plain text from Editor.js save() output.
 * @param {{ blocks?: Array<{ type: string, data: Record<string, unknown> }> }} editorData
 */
export function editorDataToPlainText(editorData) {
  if (!editorData || !Array.isArray(editorData.blocks)) return '';
  const parts = [];

  for (const block of editorData.blocks) {
    const { type, data = {} } = block;
    let chunk = '';

    switch (type) {
      case 'paragraph':
      case 'header':
        chunk = stripHtml(data.text);
        break;
      case 'list':
      case 'nestedList':
        chunk = listItemsToText(data.items);
        break;
      case 'checklist':
        if (Array.isArray(data.items)) {
          chunk = data.items
            .map((it) => {
              const mark = it.checked ? '[x]' : '[ ]';
              return `${mark} ${stripHtml(it.text || '')}`;
            })
            .join('\n');
        }
        break;
      case 'quote':
        chunk = [stripHtml(data.text), stripHtml(data.caption)].filter(Boolean).join(' — ');
        break;
      case 'warning':
        chunk = [stripHtml(data.title), stripHtml(data.message)].filter(Boolean).join(': ');
        break;
      case 'code':
      case 'codeHighlight':
        chunk = typeof data.code === 'string' ? data.code : '';
        break;
      case 'raw':
        chunk = stripHtml(data.html || '');
        break;
      case 'table':
        if (Array.isArray(data.content)) {
          chunk = data.content.map((row) => (Array.isArray(row) ? row.map((c) => stripHtml(c)).join('\t') : '')).join('\n');
        }
        break;
      case 'delimiter':
        chunk = '---';
        break;
      case 'linkTool':
        chunk = [data.link || '', data.meta?.title || ''].filter(Boolean).join(' ');
        break;
      default:
        // Fallback: shallow string fields
        chunk = Object.values(data)
          .filter((v) => typeof v === 'string')
          .map((s) => stripHtml(s))
          .join(' ');
        break;
    }

    if (chunk.trim()) parts.push(chunk.trim());
  }

  return parts.join('\n\n');
}

/** Escape for safe HTML paragraph content; preserve line breaks as <br>. */
export function plainTextToParagraphHtml(text) {
  const s = text == null ? '' : String(text);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\r\n/g, '\n')
    .replace(/\n/g, '<br>');
}

/**
 * Split AI output into paragraph block payloads (double newlines → new blocks).
 * @returns {Array<{ type: 'paragraph', data: { text: string } }>}
 */
export function plainTextToParagraphBlocks(plainText) {
  const raw = plainText == null ? '' : String(plainText);
  const segments = raw
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const list = segments.length ? segments : (raw.trim() ? [raw.trim()] : []);
  return list.map((segment) => ({
    type: 'paragraph',
    data: { text: plainTextToParagraphHtml(segment) },
  }));
}
